import type { ClientInfo, OAuthParams } from '@rentflowdev/api'
import { createContext, useContext, useState, type ReactNode } from 'react'
import { persistOAuthState, retrieveOAuthState } from '../lib/oauth-params'

interface OAuthContextValue {
	params: OAuthParams | null
	client: ClientInfo | null
	accountId: string | null
	setOAuthState: (params: OAuthParams, client: ClientInfo, accountId?: string) => void
	setAccountId: (id: string) => void
}

const OAuthContext = createContext<OAuthContextValue | null>(null)

export function OAuthProvider({ children }: { children: ReactNode }) {
	const stored = retrieveOAuthState()
	const [params, setParams] = useState<OAuthParams | null>(stored?.params ?? null)
	const [client, setClient] = useState<ClientInfo | null>(stored?.client ?? null)
	const [accountId, setAccountIdState] = useState<string | null>(stored?.accountId ?? null)

	const setOAuthState = (p: OAuthParams, c: ClientInfo, aid?: string) => {
		setParams(p)
		setClient(c)
		setAccountIdState(aid ?? null)
		persistOAuthState({ params: p, client: c, accountId: aid })
	}

	const setAccountId = (id: string) => {
		setAccountIdState(id)
		if (params && client) {
			persistOAuthState({ params, client, accountId: id })
		}
	}

	return (
		<OAuthContext value={{ params, client, accountId, setOAuthState, setAccountId }}>
			{children}
		</OAuthContext>
	)
}

export function useOAuth() {
	const ctx = useContext(OAuthContext)
	if (!ctx) throw new Error('useOAuth must be used within OAuthProvider')
	return ctx
}
